import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { CommonService } from "../../Service/CommonService";
import { InventoryApiService } from "../../Service/InventoryApiService";
import useFormValidation from "./FormValidation";
import "../Static/Login/Login.scss";
import "./AddCompanyOrUser.scss";
import Spinner from "../Common/Spinner";
import { IconButton, Tooltip } from "@mui/material";
import CreatableSelect from 'react-select/creatable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SupportApiService } from "../../Service/SupportApiService";
import { Service } from "../../Service/ApiService";
import Modal from "react-bootstrap/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ScannedWarning from "../../assets/images/icons/ScannedWarning.svg";
import Constant from "../../Constant";
import InfoIcon from '../../assets/images/icons/Info.svg'

const DEFAULT_MASH_BILL = "00% Corn, 00% Rye, 00% Wheat, 00% Malted Barley";

const EditBarrel: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [barrelData, setBarrelData] = useState<any>();
  const [facilities, setFacilities] = useState<string[]>([]);
  const [error, setError] = useState<string>("");
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const [maxStack, setMaxStack] = useState(0);
  const [maxRow, setMaxRow] = useState(0);
  const [warehouseDropdown, setWarehouseDropdown] = useState([]);
  const [stagingAreaList, setStagingAreaList] = useState([]);
  const facilityOptions = facilities.map((f: any) => ({value: f.id, label: f.facilityName}));
  const [isAnyLocationExits, setAnyLocationExist] = useState(false);

  const handleClose = (shouldResetForm = true) => {
    setShow(false); 
    setError("");

    if (shouldResetForm && barrelData) {
      setFormData({
        ...barrelData,
        status: 'PENDING_REVIEW'
      });
    }
  };
  const handleCloseWithReset = () => handleClose(true);

  const {
    formData,
    errors,
    handleChange,
    handleBlur,
    setErrors,
    validateForm,
    setFormData,
  } = useFormValidation(barrelData);

  const fetchAllWarehouse = async () => {
    try {
      const warehouses = await InventoryApiService.getWarehouseDropdown();
      setWarehouseDropdown(warehouses.data);
    } catch(error) {
      console.log("Error occurred while api calling " + error);
    }
  }

  useEffect(() => {
    fetchAllWarehouse()
    fetchBarrelById();
    fetchFacilityList();
  }, []);

  const handleCurrentLocationChange = (e) => {
    const value = e.target.value;

    if (value !== Constant.LOCATION.WAREHOUSE && value !== Constant.LOCATION.STAGING) {
      setAnyLocationExist(false);
    } else {
      setAnyLocationExist(true);
    }

    setFormData((prev) => ({
      ...prev,
      isPalletized: value === Constant.LOCATION.WAREHOUSE,
      row: "",
      depth: "",
      height: "",
      stagingArea: value === Constant.LOCATION.STAGING ? prev.stagingArea || "" : ""
    }));

    if(value === Constant.LOCATION.WAREHOUSE) {
      fetchPalletizedLocation(formData?.rackhouse);
    } else {
      fetchStagingAreaList(formData?.rackhouse);
    }
  };

  const handleWarehouseChange = (e) => {
    handleChange(e);
    const { name, value } = e.target;

    if(formData?.isPalletized) {
      fetchPalletizedLocation(value);
    } else {
      fetchStagingAreaList(value);
    }
  }

  const handleContainerHealthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;

    if (value === "EMPTY") {
      setFormData((prev: any) => ({
        ...prev,
        containerHealth: value,
        volume: 0,
        proof: 0,
        proofGallons: 0,
      }));
    } else {
      setFormData((prev: any) => ({
        ...prev,
        containerHealth: value,
        volume: barrelData?.volume ?? prev.volume,
        proof: barrelData?.proof ?? prev.proof,
        proofGallons: barrelData?.proofGallons ?? prev.proofGallons,
      }));
    }
  };

  const fetchBarrelById = async () => {
    try {
      const response = await InventoryApiService.getBarrelById(id);
      const updatedResponse = {
        ...response.data,
        id: response.data['id'],
        status: 'PENDING_REVIEW'
      };
      setBarrelData(response.data);
      setFormData(updatedResponse);

      setAnyLocationExist(response.data?.isPalletized ? true : response.data?.stagingArea ? true : false);

      if(response.data?.isPalletized) {
        fetchPalletizedLocation(response.data?.rackhouse);
      } else {
        fetchStagingAreaList(response.data?.rackhouse);
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  const fetchPalletizedLocation = async (warehouseName:any) => {
    try {
      const row = await InventoryApiService.getMaxWarehouseRow(warehouseName);
      const stack = await InventoryApiService.getMaxWarehouseStack(warehouseName);
      setMaxRow(row.data);
      setMaxStack(stack.data);
    } catch (error) {
      console.log("error occurred while api calling " + error);
    }
  }

  const fetchStagingAreaList = async (warehouse:any) => {
    try {
      const stagingAreas = await InventoryApiService.getAllWarehouseStagingArea(warehouse);
      setStagingAreaList(stagingAreas.data);
    } catch (error) {
      console.log("error occurred while api calling " + error)
    }
  }

  const stripHtml = (html = "") => (html ?? "").replace(/<\/?[^>]+(>|$)/g, "");

  const fetchFacilityList = async () => {
    try {
      const response = await SupportApiService.getFacilityList();
      setFacilities(response?.data);
      return response?.data;

    } catch (error) {
      console.log("error occurred while api calling " + error);
    }
  }

  const addFacility = async () => {

    if (formData.facility != null) {
      const requestData = {
        facilityName: formData.facility,
        isActive: true,
        isDelete: false,
      };

      const response = await SupportApiService.addFacility(requestData);
      setFormData((prev: any) => ({
        ...prev,
        facilityId: response?.data?.id,
      }));

      return response?.data?.id;
    } else
      return null;
  };

  const validateRequireFields = () => {

    let isValidationPass = true;

    const requiredFields = [
      { field: "productName", error: "Please enter product name" },
      { field: "mashBill", error: "Please enter mash bill" },
      { field: "facility", error: "Please enter facility" },
      { field: "dsp", error: "Please enter dsp" },
      { field: "barrelNumber", error: "Please enter container id" },
      { field: "fillDate", error: "Please enter fill date" },
      { field: "lotNo", error: "Please enter lot no" }
    ];

    requiredFields.forEach(({ field, error }) => {
      if (formData[field]?.length === 0 || errors[field]) {
        if (formData[field]?.length === 0) {
          setErrors((prevState) => ({ ...prevState, [field]: error }));
        }
        isValidationPass = false;
      }
    });

    return isValidationPass;
  }

  const routeBack = () => {
    history.goBack();
  };

function validateMashbill(input) {
  if (!input || String(input).trim().length === 0) {
    return { valid: false, error: "Mashbill cannot be empty" };
  }

  let clean = String(input)
    .toLowerCase()
    .replace(/,/g, " ")
    .replace(/%/g, "")
    .trim();

  // Handle leading company/brand words
  clean = clean.replace(/^[a-z\s]+(?=\d)/i, "").trim();

  // 1. Shortcut format (e.g. 70/20/10)
  if (/^[\d.]+(\s*\/\s*[\d.]+)+$/.test(clean)) {
    return { valid: true };
  }

  const tokens = clean.split(/\s+/);
  
  for (let i = 0; i < tokens.length; ) {
    const num = parseFloat(tokens[i]);
    
    if (isNaN(num)) {
      return { valid: false, error: `Invalid format near: "${tokens[i]}"` };
    }
    i++;

    // Capture the ingredient name (could be multiple words)
    const ingredientParts = [];
    while (i < tokens.length && isNaN(parseFloat(tokens[i]))) {
      ingredientParts.push(tokens[i]);
      i++;
    }

    // ERROR: Number exists but no name follows (e.g., "70% ")
    if (ingredientParts.length === 0) {
      return { valid: false, error: `Missing ingredient name after ${num}%` };
    }

    const ingredientName = ingredientParts.join("");
    // Accept any ingredient as long as it's at least 2 characters
    if (ingredientName.length < 2) {
      return { valid: false, error: `Please enter a valid ingredient name` };
    }
  }

  return { valid: true };
}

const handleMashBillChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  let value = e.target.value;

  // 1. If user totally removes the value, show prefill template
  if (value === "") {
    value = DEFAULT_MASH_BILL;
  }

  // 2. Update your existing form structure
  // This uses your existing 'handleChange' logic to update formData.mashBill
  handleChange({
    target: {
      name: "mashBill",
      value: value
    }
  } as any);

  // 3. Validation Logic
  // We only run validation if it's NOT the default template 
  // (to avoid showing "00 is not a number" errors immediately)
  if (value !== DEFAULT_MASH_BILL) {
    const result = validateMashbill(value);
    if (!result.valid) {
      setErrors((prev) => ({ ...prev, mashBill: result.error }));
    } else {
      setErrors((prev) => ({ ...prev, mashBill: "" }));
    }
  } else {
    // Clear errors if it's back to the template
    setErrors((prev) => ({ ...prev, mashBill: "" }));
  }
};
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const isFormValid = await validateRequireFields();

    if (!isFormValid) {
      setIsLoading(false);
      return;
    }

    let facilityId = formData?.facilityId;

    if (!facilityId) {
      facilityId = await addFacility();
    }
     // Processing Mash Bill to only include grains > 0%
    const processedMashBill = (formData?.mashBill || "")
        .split(",")
        .map((item: string) => item.trim())
        .filter((item: string) => {
            // Extracts the percentage value. E.g. "70% Corn" -> "70"
            const match = item.match(/^(\d+)%/);
            if (match) {
                return parseInt(match[1], 10) > 0;
            }
            return item.length > 0; // Keep non-percentage items if they exist
        })
        .join(", ");
    const payload = {
      ...formData,
      facilityId,
      mashBill: processedMashBill
    };

    try {

      // CHECK BEFORE EDIT (ADDED)
      const response = await Service.fetchCustomerServiceRequestBarrelId(id);
      let isUsed = response.data;
      if (isUsed && !show) {
        handleShow();
        setIsLoading(false);
        return;
      }

      await InventoryApiService.editBarrel(payload);

      CommonService.Toast.fire({
        title: "Container Updated Successfully",
        icon: "success",
      });
      history.goBack();
    } catch (error: any) {
      if(error.response.data.errorMessage){
        CommonService.Toast.fire({
          title: error.response.data.errorMessage,
          icon: "error",
        });
      } else{
        CommonService.Toast.fire({
          title: "There is some technical problem.",
          icon: "error",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="add-company-user-wrapper usersList rounded-0 edit-barrels-wrapper">
        {CommonService.isAdmin() && (
          <div className="usersList__header--wrapper d-flex flex-wrap align-items-center w-100">
            <div className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2 position-relative">
              <div className="d-flex align-items-center gap-2 flex-wrap left-wrapper">
                <div className="back__button" onClick={routeBack}>
                  <IconButton
                    aria-label="ArrowBackIcon"
                    sx={{ backgroundColor: "#FFF" }}
                  >
                    <ArrowBackIcon sx={{ color: "#1C1B1F" }} />
                  </IconButton>
                </div>

                <div className="header--title">
                  <h1 className="title mb-0">
                    Edit Container Details
                  </h1>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="add-company-user-form-wrapper p-0">
          <form
            className="form__inner edit-inventory-form py-5 mx-auto"
            onSubmit={onSubmit}
            noValidate
          >
            <div className="row g-4">
              <>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="productName" className="form-label">
                    <strong>Product Name{" "}</strong> <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="productName"
                    name="productName"
                    placeholder="Enter product name"
                    value={formData?.productName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.productName ? "is_invalid" : ""}`}
                  />
                  {errors.productName && (
                    <div className="error-message">{errors.productName}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="barrelNumber" className="form-label">
                    <strong>Container Id{" "}</strong> <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="barrelNumber"
                    name="barrelNumber"
                    placeholder="Enter container number"
                    value={formData?.barrelNumber}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={true}
                    className={`form-control ${errors.barrelNumber ? "is_invalid" : ""}`}
                  />
                  {errors.barrelNumber && (
                    <div className="error-message">{errors.barrelNumber}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="lotNo" className="form-label">
                    <strong>Lot No{" "}</strong> <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="lotNo"
                    name="lotNo"
                    placeholder="Enter lot no"
                    value={formData?.lotNo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={true}
                    className={`form-control ${errors.lotNo ? "is_invalid" : ""
                      }`}
                  />
                  {errors.lotNo && (
                    <div className="error-message">{errors.lotNo}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="barrelCost" className="form-label">
                    <strong>Container Cost</strong>
                  </label>
                  <input
                    type="text"
                    id="barrelCost"
                    placeholder="Enter container cost"
                    name="barrelCost"
                    value={formData?.barrelCost}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="sellCost" className="form-label">
                    <strong>Sell Cost</strong>
                  </label>
                  <input
                    type="text"
                    id="sellCost"
                    placeholder="Enter sell cost"
                    name="sellCost"
                    value={formData?.sellCost}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="insuranceValue" className="form-label">
                    <strong>Insurance Value</strong>
                  </label>
                  <input
                    type="text"
                    id="insuranceValue"
                    placeholder="Enter insurance value"
                    name="insuranceValue"
                    value={formData?.insuranceValue}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="producer" className="form-label">
                    <strong>Producer</strong>
                  </label>
                  <input
                    type="text"
                    id="producer"
                    name="producer"
                    placeholder="Enter producer"
                    value={formData?.producer}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.producer ? "is_invalid" : ""
                      }`}
                  />
                  {errors.producer && (
                    <div className="error-message">{errors.producer}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="mashBill" className="form-label">
                    <strong>Mash Bill</strong> <span className="star-required text-danger">*
                      <Tooltip title={
                        <>
                          Enter Mash Bill in the following format:
                          <br />
                          00% Ingredient Name, 00% Ingredient Name
                          <br />
                          Example:
                          <br />
                          20% Corn, 30% Rye, 40% Wheat, 10% Malted Barley
                        </>
                      }>
                        <img src={InfoIcon} alt="Info" className="inline-block ms-2"/>
                      </Tooltip>
                    </span>
                  </label>
                  <input
                    type="text"
                    id="mashBill"
                    name="mashBill"
                    placeholder="Enter mash bill"
                    value={formData?.mashBill}
                    onChange={handleMashBillChange}
                    onBlur={handleBlur}
                    onFocus={(e) => {
                      // If the value is the template, auto-select it for easy replacement
                      if (formData?.mashBill === DEFAULT_MASH_BILL) {
                        e.target.select();
                      }
                    }}
                    className={`form-control ${errors.mashBill ? "is_invalid" : ""
                      }`}
                  />
                  {errors.mashBill && (
                    <div className="error-message">{errors.mashBill}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="ttbType" className="form-label">
                    <strong>TTB Type{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="ttbType"
                    name="ttbType"
                    placeholder="Enter ttb type"
                    value={formData?.ttbType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.ttbType ? "is_invalid" : ""
                      }`}
                  />
                  {errors.ttbType && (
                    <div className="error-message">{errors.ttbType}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="ttbClass" className="form-label">
                    <strong>TTB Class{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="ttbClass"
                    name="ttbClass"
                    placeholder="Enter ttb class"
                    value={formData?.ttbClass}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.ttbClass ? "is_invalid" : ""
                      }`}
                  />
                  {errors.ttbClass && (
                    <div className="error-message">{errors.ttbClass}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="fillDate" className="form-label">
                    <strong>Fill Date</strong> <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    id="fillDate"
                    name="fillDate"
                    placeholder="Enter fill date"
                    value={formData?.fillDate}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={true}
                    className={`form-control ${errors.fillDate ? "is_invalid" : ""
                      }`}
                  />
                  {errors.fillDate && (
                    <div className="error-message">{errors.fillDate}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="facility" className="form-label">
                    <strong>Facility</strong>
                  </label>
                  <CreatableSelect
                    isClearable
                    options={facilityOptions}
                    value={
                      formData?.facilityId
                        ? { value: formData?.facilityId, label: formData?.facility }
                        : formData?.facility
                          ? { value: formData?.facility, label: formData?.facility }
                          : null
                    }
                    onChange={(selected: any) => {
                      if (selected) {
                        setFormData((prev: any) => ({
                          ...prev,
                          facility: selected.label,
                          facilityId: selected.value,
                        }));
                      } else {
                        setFormData((prev: any) => ({
                          ...prev,
                          facility: "",
                          facilityId: null
                        }));
                      }
                    }}
                    onCreateOption={(inputValue: string) => {
                      // User typed a NEW facility
                      setFormData((prev: any) => ({
                        ...prev,
                        facility: inputValue,
                        facilityId: null, // indicate NEW
                      }));
                    }}
                    placeholder="Select or create facility"
                  />

                  {errors.facility && (
                    <div className="error-message">{errors.facility}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="barrelSize" className="form-label">
                    <strong>Container Size{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="barrelSize"
                    name="barrelSize"
                    placeholder="Enter container size"
                    value={formData?.barrelSize}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.barrelSize ? "is_invalid" : ""
                      }`}
                  />
                  {errors.barrelSize && (
                    <div className="error-message">{errors.barrelSize}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="volume" className="form-label">
                    <strong>Volume</strong>
                  </label>
                  <input
                    type="text"
                    id="volume"
                    name="volume"
                    placeholder="Enter volume"
                    value={formData?.volume}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.volume ? "is_invalid" : ""}`}
                  />
                  {errors.volume && (
                    <div className="error-message">{errors.volume}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="proof" className="form-label">
                    <strong>Proof{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="proof"
                    name="proof"
                    placeholder="Enter proof"
                    value={formData?.proof}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.proof ? "is_invalid" : ""
                      }`}
                  />
                  {errors.proof && (
                    <div className="error-message">{errors.proof}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="proofGallons" className="form-label">
                    <strong>Proof Gallons{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="proofGallons"
                    name="proofGallons"
                    placeholder="Enter proof gallons"
                    value={formData?.proofGallons}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.proofGallons ? "is_invalid" : ""
                      }`}
                  />
                  {errors.proofGallons && (
                    <div className="error-message">{errors.proofGallons}</div>
                  )}
                </div>
               
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="rackhouse" className="form-label">
                    <strong>Warehouse</strong>
                  </label>

                  <select
                    id="rackhouse"
                    name="rackhouse"
                    value={formData?.rackhouse}
                    onChange={handleWarehouseChange}
                    className={`form-select ${errors.rackhouse ? "is-invalid" : ""}`}
                  >

                  <option value="">Select Warehouse</option>

                    {warehouseDropdown.map((warehouse) => (
                      <option key={warehouse.warehouseId} value={warehouse.warehouseName}>
                        {warehouse.warehouseName}
                      </option>
                    ))}
                  </select>

                  {errors?.rackhouse && (
                    <div className="error-message">{errors.rackhouse}</div>
                  )}
                </div>

                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="current-location" className="form-label">
                    <strong>Select Location </strong>
                  </label>

                  <select
                    id="current-location"
                    name="isPalletized"
                    value={(formData?.isPalletized && isAnyLocationExits) ? "In Rack" : (!formData?.isPalletized && isAnyLocationExits) ? "In Staging" : "Select Location"}
                    onChange={handleCurrentLocationChange}
                    disabled={!formData?.rackhouse && !formData?.isPalletized}
                    className={`form-select ${errors.isPalletized ? "is-invalid" : ""}`}
                  >
                    <option value="">Select Location </option>
                    <option value="In Staging">In Staging</option>
                    <option value="In Rack">In Rack</option>
                  </select>

                  {errors.isPalletized && (
                    <div className="error-message">{errors.isPalletized}</div>
                  )}
                </div>

                {(formData?.isPalletized && isAnyLocationExits) && (
                <>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="row" className="form-label">
                    <strong>Row{" "}</strong>
                  </label>
                  <select
                    id="row"
                    name="row"
                    value={formData?.row || ""}
                    onChange={handleChange}
                    className={`form-control form-select ${errors.row ? "is-invalid" : ""}`}
                  >
                    <option value="">Select row</option>

                    {Array.from({ length: maxRow }, (_, index) => {
                      const value = index + 1;
                      return (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      );
                    })}
                  </select>
                  {errors.row && (
                    <div className="error-message">{errors.row}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                <label htmlFor="depth" className="form-label">
                  <strong>Depth </strong>
                </label>

                <select
                  id="depth"
                  name="depth"
                  value={formData?.depth || ""}
                  onChange={handleChange}
                  className={`form-control form-select ${errors.depth ? "is-invalid" : ""}`}
                >
                  <option value="">Select depth</option>

                  {Array.from({ length: maxStack }, (_, index) => {
                    const value = index + 1;
                    return (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    );
                  })}
                </select>

                {errors.depth && (
                  <div className="error-message">{errors.depth}</div>
                )}
              </div>

                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="height" className="form-label">
                    <strong>Height{" "}</strong>
                  </label>
                  <select
                    id="height"
                    name="height"
                    value={formData?.height || ""}
                    onChange={handleChange}
                    className={`form-control form-select ${errors.height ? "is-invalid" : ""}`}
                  >
                    <option value="">Select Height</option>

                    {Array.from({ length: 6 }, (_, index) => index + 1).map((height) => (
                      <option key={height} value={height}>
                        {height}
                      </option>
                    ))}
                  </select>

                  {errors.height && (
                    <div className="error-message">{errors.height}</div>
                  )}
                </div>
                </>)}
                {(!formData?.isPalletized && isAnyLocationExits) && (
                  <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="stagingArea" className="form-label">
                    <strong>Staging Area </strong>
                  </label>

                  <select
                    id="staging-area"
                    name="stagingArea"
                    value={formData?.stagingArea}
                    onChange={handleChange}
                    className={`form-control form-select ${errors.stagingArea ? "is-invalid" : ""}`}
                  >
                    <option value="">Select Staging Area</option>

                    {stagingAreaList?.map((staging) => (
                      <option key={staging} value={staging}>
                        {staging}
                      </option>
                      
                    ))}
                  </select>

                  {errors.stagingArea && (
                    <div className="error-message">{errors.stagingArea}</div>
                  )}
                </div>
                )}

                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="totalLotPg" className="form-label">
                    <strong>Total Lot Pg{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="totalLotPg"
                    name="totalLotPg"
                    placeholder="Enter totalLotPg"
                    value={formData?.totalLotPg}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.totalLotPg ? "is_invalid" : ""
                      }`}
                  />
                  {errors.totalLotPg && (
                    <div className="error-message">{errors.totalLotPg}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="dsp" className="form-label">
                    <strong>Dsp{" "}</strong> <span className="star-required text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="dsp"
                    name="dsp"
                    placeholder="Enter dsp"
                    value={formData?.dsp}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.dsp ? "is_invalid" : ""
                      }`}
                  />
                  {errors.dsp && (
                    <div className="error-message">{errors.dsp}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="stage" className="form-label">
                    <strong>Stage{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="stage"
                    name="stage"
                    placeholder="Enter stage"
                    value={formData?.stage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.stage ? "is_invalid" : ""
                      }`}
                  />
                  {errors.stage && (
                    <div className="error-message">{errors.stage}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="location" className="form-label">
                    <strong>Location{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    placeholder="Enter location"
                    value={formData?.location}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.location ? "is_invalid" : ""
                      }`}
                  />
                  {errors.location && (
                    <div className="error-message">{errors.location}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="state" className="form-label">
                    <strong>State{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    placeholder="Enter state"
                    value={formData?.state}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.state ? "is_invalid" : ""
                      }`}
                  />
                  {errors.state && (
                    <div className="error-message">{errors.state}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="country" className="form-label">
                    <strong>Country{" "}</strong>
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    placeholder="Enter country"
                    value={formData?.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${errors.country ? "is_invalid" : ""
                      }`}
                  />
                  {errors.country && (
                    <div className="error-message">{errors.country}</div>
                  )}
                </div>
                {formData?.containerType === "BARREL" && (
                  <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                    <label className="form-label">
                      <strong>Container Condition</strong>
                    </label>

                    <div className="d-flex align-items-center gap-3 mt-1">
                      <div className="form-check ps-0">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="containerCooperage"
                          id="container-cooperage-reused"
                          value="REUSED"
                          checked={formData?.containerCooperage === "REUSED"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="container-cooperage-reused"
                        >
                          Reused
                        </label>
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="containerCooperage"
                          id="container-cooperage-new"
                          value="NEW"
                          checked={formData?.containerCooperage === "NEW"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="container-cooperage-new"
                        >
                          New
                        </label>
                      </div>
                    </div>

                    {errors.containerCooperage && (
                      <div className="error-message">{errors.containerCooperage}</div>
                    )}
                  </div>
                )}
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="containerHealth" className="form-label">
                    <strong>Container Health</strong>
                  </label>
                  <select
                    id="containerHealth"
                    name="containerHealth"
                    value={formData?.containerHealth || ""}
                    onChange={handleContainerHealthChange}
                    className={`form-select ${errors.containerHealth ? "is_invalid" : ""}`}
                  >
                    <option disabled>Select Container Health</option>
                    <option value="DAMAGED">Damaged</option>
                    <option value="UNDAMAGED" disabled>Undamaged</option>
                    <option value="LEAKING">Leaking</option>
                    <option value="FILLED">Filled</option>
                    <option value="EMPTY">Empty</option>
                  </select>
                  {errors.containerHealth && (
                    <div className="error-message">{errors.containerHealth}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 mb-0">
                  <label htmlFor="description" className="form-label">
                    <strong>Container Description</strong>
                  </label>

                  <textarea
                    required
                    className={`form-control ${errors.description ? "is_invalid" : ""}`}
                    aria-label="With textarea"
                    id="description"
                    name="description"
                    placeholder="Enter text here..."
                    value={stripHtml(formData?.description)}
                    onChange={handleChange}
                    rows={4}
                    maxLength={255}
                    style={{ resize: "none" }}
                  />

                  <span className="text-length">
                    {stripHtml(formData?.description)?.length ?? 0}/255
                  </span>

                  {errors.description && (
                    <div className="error-message">{errors.description}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 mb-0">
                  <label htmlFor="mashBillDescription" className="form-label">
                    <strong>Mashbill Description</strong>
                  </label>

                  <textarea
                    required
                    className={`form-control ${errors.mashBillDescription ? "is_invalid" : ""}`}
                    aria-label="With textarea"
                    id="mashBillDescription"
                    name="mashBillDescription"
                    placeholder="Enter text here..."
                    value={stripHtml(formData?.mashBillDescription)}
                    onChange={handleChange}
                    rows={4}
                    maxLength={255}
                    style={{ resize: "none" }}
                  />

                  {errors.mashBillDescription && (
                    <div className="error-message">{errors.mashBillDescription}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="tastingNote" className="form-label">
                    <strong>Tasting Note{" "}</strong>
                  </label>
                  <textarea
                    required
                    className={`form-control ${errors.tastingNote ? "is_invalid" : ""}`}
                    aria-label="With textarea"
                    id="tastingNote"
                    name="tastingNote"
                    placeholder="Tasting Notes are here..."
                    value={formData?.tastingNote}
                    onChange={handleChange}
                    rows={4}
                    maxLength={255}
                    style={{ resize: "none" }}
                  />
                  <span className="text-length">{formData?.tastingNote?.length ?? 0}/255</span>
                  {errors.tastingNote && (
                    <div className="error-message">{errors.tastingNote}</div>
                  )}
                </div>
                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="isShipped" className="form-label">
                    <strong>Shipped</strong>
                  </label>
                  <div className="d-flex align-items-center gap-3 mt-1">
                    <div className="form-check ps-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="isShipped"
                        id="isShipped-yes"
                        value="true"
                        checked={formData?.isShipped === true}
                        onChange={() => setFormData({ ...formData, isShipped: true })}
                      />
                      <label className="form-check-label" htmlFor="isShipped-yes">Yes</label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="isShipped"
                        id="isShipped-no"
                        value="false"
                        checked={formData?.isShipped === false}
                        onChange={() => setFormData({ ...formData, isShipped: false })}
                      />
                      <label className="form-check-label" htmlFor="isShipped-no">No</label>
                    </div>
                  </div>
                  {errors.isShipped && (
                    <div className="error-message">{errors.isShipped}</div>
                  )}
                </div>

                <div className="edit-barrels-form-group col-12 col-sm-6 col-lg-4 col-xl-3 position-relative mb-0">
                  <label htmlFor="isManagedByWhitedog" className="form-label">
                    <strong>Whitedog Managed</strong>
                  </label>
                  <div className="d-flex align-items-center gap-3 mt-1">
                    <div className="form-check ps-0">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="isManagedByWhitedog"
                        id="isManagedByWhitedog-yes"
                        value="true"
                        checked={formData?.isManagedByWhitedog === true}
                        onChange={() => setFormData({ ...formData, isManagedByWhitedog: true })}
                      />
                      <label className="form-check-label" htmlFor="isManagedByWhitedog-yes">Yes</label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="isManagedByWhitedog"
                        id="isManagedByWhitedog-no"
                        value="false"
                        checked={formData?.isManagedByWhitedog === false}
                        onChange={() => setFormData({ ...formData, isManagedByWhitedog: false })}
                      />
                      <label className="form-check-label" htmlFor="isManagedByWhitedog-no">No</label>
                    </div>
                  </div>
                  {errors.isManagedByWhitedog && (
                    <div className="error-message">{errors.isManagedByWhitedog}</div>
                  )}
                </div>

              </>
              <div className="col-12 mb-3">
                <div className="form__btn--group">
                  <button
                    type="button"
                    onClick={routeBack}
                    className="btn secondary-btn register-btn mb-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn primary-btn register-btn mb-2"
                    disabled={isLoading}
                  >
                    {isLoading && <Spinner />}
                    {"Submit"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <Modal
        show={show}
        onHide={handleCloseWithReset}
        className="modal__wd modal__wd--submitSellReq barrel-assignment-modal signup-success-modal"
        size="lg"
        centered
      >
        <Modal.Header className="align-items-center justify-content-between">
          <Modal.Title className="modal-title">Confirm Container Detail Update</Modal.Title>
          <button onClick={handleCloseWithReset} type="button" className="p-0 bg-transparent border-0 outline-0" data-bs-dismiss="modal" aria-label="Close">
            <CloseIcon sx={{ color: "#ffffff" }} />
          </button>
        </Modal.Header>

        <Modal.Body>
          <div className="form__inner scanned-popup-wrapper">
            {/* <div className="text-center d-flex justify-content-center">
              <span className="warning-icon-wrapper">
              </span>
            </div> */}
            <div className="text-center">
                <span className="warning-icon-wrapper mx-auto">
                 <img src={ScannedWarning} alt="Product Checkout icon" />
                </span>
              </div>
            <h2 className="modal-body__title text-center">
              Are you sure you want to update Container details?
            </h2>
            <p className="modal-text">
              This Container is associated with one or more service requests.
              Proceeding will only update the container details.
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer className="justify-content-center">
          <div className="btn-group d-flex justify-content-center flex-wrap w-100 gap-3">
            {/* <div className="col-6 ps-0"> */}
              <button className="secondary-btn" onClick={handleCloseWithReset} disabled={isLoading} style={{minWidth:"190px"}}>
                No
              </button>
            {/* </div>

            <div className="col-6 pe-0"> */}
              <button className="primary-btn" disabled={isLoading}
              onClick={(e) => {
                handleClose(false);  // close popup but keep current form values
                onSubmit(e);    // reuse same method
              }} style={{minWidth:"190px"}}>
                {isLoading && <Spinner />} Yes
              </button>
            {/* </div> */}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditBarrel;
